<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-6xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4 flex justify-between items-center">
        <div>
          <h1 class="sci-page-title-only">Gestão de Cartões Black</h1>
          <p class="text-sm text-slate-500">Configure limites, senhas e acompanhe o consumo em tempo real</p>
        </div>
      </div>

      <!-- DASHBOARD DE MONITORAMENTO RÁPIDO -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-slate-900 rounded-xl p-6 border border-white/10 shadow-xl overflow-hidden relative group">
           <div class="relative z-10">
             <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest block mb-1">Total de Cartões</span>
             <h3 class="text-3xl font-black text-white">{{ cards.length }}</h3>
           </div>
            <v-icon class="absolute -right-12 top-1/2" style="color: rgba(255,255,255,0.08); font-size: 60px; transform: translateY(-50%) rotate(-15deg);">mdi-card-bulleted</v-icon>
        </div>
        <div class="bg-slate-900 rounded-xl p-6 border border-white/10 shadow-xl overflow-hidden relative group">
           <div class="relative z-10">
             <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest block mb-1">Limite Total do Mês</span>
             <h3 class="text-2xl font-black text-white">R$ {{ totalLimit.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }}</h3>
           </div>
            <v-icon class="absolute -right-12 top-1/2" style="color: rgba(255,255,255,0.08); font-size: 60px; transform: translateY(-50%) rotate(-15deg);">mdi-cash-multiple</v-icon>
        </div>
        <div class="bg-slate-900 rounded-xl p-6 border border-white/10 shadow-xl overflow-hidden relative group">
           <div class="relative z-10">
             <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest block mb-1">Total Utilizado</span>
             <h3 class="text-2xl font-black text-orange-500">R$ {{ totalUsed.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }}</h3>
           </div>
            <v-icon class="absolute -right-12 top-1/2" style="color: rgba(255,255,255,0.08); font-size: 60px; transform: translateY(-50%) rotate(-15deg);">mdi-trending-up</v-icon>
        </div>
        <div class="bg-slate-900 rounded-xl p-6 border border-white/10 shadow-xl overflow-hidden relative group">
           <div class="relative z-10">
             <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest block mb-1">Saldo Disponível</span>
             <h3 class="text-2xl font-black text-emerald-400">R$ {{ (totalLimit - totalUsed).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }}</h3>
           </div>
            <v-icon class="absolute -right-12 top-1/2" style="color: rgba(255,255,255,0.08); font-size: 60px; transform: translateY(-50%) rotate(-15deg);">mdi-check-decagram</v-icon>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- FORMULÁRIO (COL 1) -->
        <div class="lg:col-span-1">
          <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm sticky top-8">
            <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-credit-card-plus</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                    {{ cardData.card_id ? 'EDITAR CARTÃO' : 'EMITIR NOVO CARTÃO' }}
                </h2>
            </div>
            
            <div class="p-6">
                <v-form @submit.prevent="createOrUpdate">
                  <div class="space-y-4">
                    <div>
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Prefeitura</label>
                      <v-autocomplete
                        v-model="cardData.prefecture_id"
                        :items="prefectures"
                        item-text="prefecture_social_name"
                        item-value="prefecture_id"
                        placeholder="Selecione a prefeitura..."
                        outlined dense hide-details class="custom-input"
                        @change="onPrefectureChange"
                      />
                    </div>

                    <div>
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Portador (Cliente)</label>
                      <v-autocomplete
                        v-model="cardData.client_id"
                        :items="availableClients"
                        item-text="client_name"
                        item-value="client_id"
                        placeholder="Escolha o portador..."
                        :disabled="!cardData.prefecture_id"
                        outlined dense hide-details class="custom-input"
                      />
                    </div>

                    <div>
                      <div class="flex justify-between items-center mb-1">
                        <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Número do Cartão (16 dígitos)</label>
                        <button type="button" @click="generateCardNumber" class="text-[9px] font-black text-orange-500 hover:text-orange-600 uppercase tracking-tighter">
                          [ GERAR NOVO ]
                        </button>
                      </div>
                      <v-text-field
                        v-model="cardData.card_number"
                        v-mask="'#### #### #### ####'"
                        placeholder="0000 0000 0000 0000"
                        outlined dense hide-details class="custom-input"
                      />
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Senha (PIN)</label>
                        <v-text-field
                          v-model="cardData.card_password"
                          type="password"
                          placeholder="Ex: 1234"
                          outlined dense hide-details class="custom-input"
                        />
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Dia de Reinício</label>
                        <v-select
                          v-model="cardData.reset_day"
                          :items="[1,2,3,4,5,10,15,20,25]"
                          outlined dense hide-details class="custom-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Limite Mensal (R$)</label>
                      <v-text-field
                        v-model="cardData.card_limit"
                        placeholder="0,00"
                        prefix="R$"
                        outlined dense hide-details class="custom-input"
                      />
                    </div>
                  </div>

                  <div class="mt-8 pt-6 border-t border-slate-100 space-y-2">
                    <button 
                      type="submit"
                      class="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] py-3 rounded shadow-lg transition-all uppercase flex items-center justify-center gap-2"
                    >
                      <v-icon small color="white">mdi-check</v-icon>
                      {{ cardData.card_id ? 'ATUALIZAR CARTÃO' : 'ATIVAR CARTÃO' }}
                    </button>
                    <button 
                      v-if="cardData.card_id"
                      type="button"
                      @click="newData"
                      class="w-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-[11px] py-3 rounded transition-all uppercase"
                    >
                      CANCELAR
                    </button>
                  </div>
                </v-form>
            </div>
          </section>
        </div>

        <!-- LISTAGEM E MONITORAMENTO (COL 2-3) -->
        <div class="lg:col-span-2">
          <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center justify-between" style="background-color: #313131;">
                  <div class="flex items-center gap-3">
                    <v-icon color="orange" small>mdi-monitor-dashboard</v-icon>
                    <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ACOMPANHAMENTO DE LIMITES</h2>
                  </div>
                  <div class="relative w-64">
                    <input 
                      v-model="search"
                      placeholder="PESQUISAR..."
                      class="bg-white/10 border border-white/20 rounded-lg py-1.5 pl-4 pr-4 text-[10px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                    >
                  </div>
              </div>

              <div class="p-0">
                  <v-data-table
                    :headers="headers"
                    :items="cards"
                    :search="search"
                    :loading="loader"
                    class="custom-table"
                  >
                    <template v-slot:item.card_number="{ item }">
                      <div class="flex items-center gap-3 py-2">
                        <div class="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <v-icon color="slate-400">mdi-credit-card-chip-outline</v-icon>
                        </div>
                        <div class="flex flex-col">
                          <span class="text-xs font-black text-slate-700 tracking-wider">{{ item.card_number }}</span>
                          <span class="text-[10px] text-slate-400 uppercase font-bold">{{ item.client_name }}</span>
                        </div>
                      </div>
                    </template>

                    <template v-slot:item.usage="{ item }">
                      <div class="w-full py-4 min-w-[200px]">
                        <div class="flex justify-between items-end mb-1">
                          <span class="text-[10px] font-bold text-slate-400 uppercase">CONSUMO MENSAL</span>
                          <span class="text-[11px] font-black" :class="getUsageColor(item)">{{ calculatePercent(item) }}%</span>
                        </div>
                        <v-progress-linear
                          :value="calculatePercent(item)"
                          height="8"
                          rounded
                          :color="getUsageProgressColor(item)"
                          background-color="slate-100"
                        ></v-progress-linear>
                        <div class="flex justify-between mt-1 text-[10px] font-bold text-slate-500">
                          <span>R$ {{ Number(item.card_used_limit).toFixed(2) }}</span>
                          <span>LIMITE: R$ {{ Number(item.card_limit).toFixed(2) }}</span>
                        </div>
                      </div>
                    </template>

                    <template v-slot:item.reset="{ item }">
                       <div class="flex flex-col items-center">
                         <span class="text-xs font-bold text-slate-600">DIA {{ item.reset_day }}</span>
                         <span class="text-[9px] text-slate-400 uppercase tracking-tighter">REINÍCIO</span>
                       </div>
                    </template>

                    <template v-slot:item.action="{ item }">
                      <div class="flex justify-end gap-1 px-2">
                        <button @click="loadData(item)" class="p-2 hover:bg-slate-100 text-slate-500 rounded-lg transition-colors">
                          <v-icon small>mdi-cog-outline</v-icon>
                        </button>
                        <button @click="deleteItem(item.card_id)" class="p-2 hover:bg-red-50 text-red-400 rounded-lg transition-colors">
                          <v-icon small>mdi-delete-outline</v-icon>
                        </button>
                      </div>
                    </template>
                  </v-data-table>
              </div>
          </section>
        </div>
      </div>
    </div>

    <!-- Feedback -->
    <v-snackbar v-model="snack.show" :color="snack.color" timeout="3000" top right>
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-[11px] font-bold uppercase tracking-wider">{{ snack.message }}</span>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data: () => ({
    search: "",
    loader: false,
    cards: [],
    clients: [],
    cardData: {
      card_id: null,
      card_number: "",
      card_password: "",
      card_limit: 0,
      reset_day: 1,
      client_id: null,
      prefecture_id: null
    },
    prefectures: [],
    snack: {
      show: false,
      message: "",
      color: "success"
    },
    headers: [
      { text: "Cartão / Portador", align: "start", value: "card_number", sortable: true },
      { text: "Monitoramento de Uso", align: "start", value: "usage", sortable: true, width: "300px" },
      { text: "Ciclo", align: "center", value: "reset", sortable: true },
      { text: "", align: "right", value: "action", sortable: false }
    ]
  }),
  computed: {
    totalLimit() {
      return this.cards.reduce((acc, c) => acc + Number(c.card_limit), 0);
    },
    totalUsed() {
      return this.cards.reduce((acc, c) => acc + Number(c.card_used_limit), 0);
    },
    availableClients() {
      if (!this.cardData.prefecture_id) return [];
      
      // Filtrar clientes pela prefeitura selecionada
      let list = this.clients.filter(c => c.prefecture_id === this.cardData.prefecture_id);
      
      // Se estiver cadastrando um novo cartão, remover clientes que já possuem um card ID vinculado
      // Se estiver editando, permitir o cliente atual do cartão
      const linkedClientIds = this.cards.map(c => c.client_id);
      
      return list.filter(c => {
        // Se o cliente NÃO tem cartão vinculado, ele está disponível
        if (!linkedClientIds.includes(c.client_id)) return true;
        
        // Se estamos EDITANDO e este é o cliente do cartão atual, ele deve aparecer
        if (this.cardData.card_id && c.client_id === this.cardData.client_id) return true;
        
        return false;
      });
    }
  },
  created() {
    this.newData();
    this.fetchClients();
    this.fetchCards();
    this.fetchPrefectures();
  },
  methods: {
    fetchClients() {
      this.$http.get("/settings/black-cards/clients").then(r => {
        this.clients = r.data;
      });
    },
    fetchPrefectures() {
      this.$http.get("/prefectures").then(r => {
        this.prefectures = r.data;
      });
    },
    fetchCards() {
      this.loader = true;
      this.$http.get("/settings/black-cards").then(r => {
        this.cards = r.data;
        this.loader = false;
      }).catch(() => { this.loader = false; });
    },
    calculatePercent(item) {
      if (!item.card_limit || item.card_limit == 0) return 0;
      const pc = (Number(item.card_used_limit) / Number(item.card_limit)) * 100;
      return Math.min(pc, 100).toFixed(1);
    },
    getUsageColor(item) {
      const pc = this.calculatePercent(item);
      if (pc > 90) return 'text-red-600';
      if (pc > 70) return 'text-orange-500';
      return 'text-slate-700';
    },
    getUsageProgressColor(item) {
      const pc = this.calculatePercent(item);
      if (pc > 90) return '#ef4444';
      if (pc > 70) return '#f97316';
      return '#3b82f6';
    },
    onPrefectureChange() {
      // Quando trocar a prefeitura, limpa o portador selecionado
      this.cardData.client_id = null;
    },
    newData() {
      this.cardData = {
        card_id: null,
        card_number: "",
        card_password: "",
        card_limit: 0,
        reset_day: 1,
        client_id: null,
        prefecture_id: null
      };
      this.generateCardNumber();
    },
    generateCardNumber() {
      let num = "";
      for (let i = 0; i < 16; i++) {
        num += Math.floor(Math.random() * 10).toString();
      }
      this.cardData.card_number = num;
    },
    loadData(item) {
      this.cardData = { ...item };
    },
    createOrUpdate() {
      this.$store.dispatch("setLoader", true);
      const data = { ...this.cardData };
      data.card_number = data.card_number.replace(/\s/g, ''); // Remove spaces

      const request = data.card_id 
        ? this.$http.put(`/settings/black-cards/${data.card_id}`, data)
        : this.$http.post("/settings/black-cards", data);

      request.then(r => {
        this.snack.show = true;
        this.snack.message = r.data.result;
        this.snack.color = "success";
        this.fetchCards();
        this.newData();
        this.$store.dispatch("setLoader", false);
      }).catch(e => {
        this.snack.show = true;
        this.snack.message = e.message;
        this.snack.color = "error";
        this.$store.dispatch("setLoader", false);
      });
    },
    deleteItem(id) {
      if (confirm("Deseja cancelar e excluir este cartão permanentemente?")) {
        this.$http.delete(`/settings/black-cards/${id}`).then(r => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.fetchCards();
        });
      }
    }
  }
};
</script>

<style scoped>
.custom-input >>> .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 8px !important;
}
.custom-input >>> fieldset {
  border-color: #e2e8f0 !important;
}
.custom-table >>> th {
  background-color: #f8fafc !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  color: #64748b !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  padding: 16px !important;
}
.custom-table >>> td {
  padding: 8px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>
